import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { computeStampGrid } from '../../../core/utils/stamp-grid.util';

export interface ICardSealPreview { url: string; bg: string; }

/**
 * Reverso de la tarjeta de lealtad (02-decisiones-ui.md): color de fondo, casillas con la forma elegida
 * (4–20; 2 filas hasta 14 y 3 filas desde 15), la última es PREMIO; texto en un solo estilo (DM Sans SemiBold).
 * Se dibuja sobre la tarjeta base de 392×224 y escala con el ancho del contenedor (container queries).
 */
@Component({
  selector: 'app-card-back',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="card" [style.background]="backgroundColor()">
      <div class="grid" [style.top.em]="grid().top" [style.gap.em]="grid().gap">
        @for (row of rows(); track $index) {
          <div class="cb-row" [style.gap.em]="grid().gap">
            @for (slot of row; track slot.index) {
              <app-stamp-slot class="slot" [style.width.em]="grid().size" [style.height.em]="grid().size"
                [shape]="slotShape()" [color]="slotColor()" [state]="slot.state"
                [imageUrl]="slot.seal?.url" [imageBg]="slot.seal?.bg" />
            }
          </div>
        }
      </div>
      @if (backText()) {
        <div class="text-box" [style.top.em]="grid().textTop"><p class="text" [style.color]="textColor()">{{ backText() }}</p></div>
      }
    </div>
  `,
  styles: [`
    :host{display:block;container-type:inline-size;aspect-ratio:392/224;width:100%}
    .card{position:relative;width:100%;height:100%;font-size:calc(100cqw / 392);border-radius:20em;overflow:hidden;
      box-shadow:var(--shadow, var(--cr-card-physical))}
    .grid{position:absolute;left:0;right:0;display:flex;flex-direction:column;align-items:center}
    .cb-row{display:flex;justify-content:center}
    .slot{flex:none}
    .text-box{position:absolute;left:30em;right:30em}
    .text{margin:0;text-align:center;font:600 13em/1.4 var(--cr-font-body);
      display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:break-word}
  `],
})
export class CardBackComponent {
  readonly backgroundColor = input<string>('#1E1B16');
  readonly slotColor = input<string>('#EFE6D4');
  readonly slotShape = input<string>('circle');
  readonly stampsRequired = input<number>(10);
  readonly textColor = input<string>('#FFFFFF');
  readonly backText = input<string | null | undefined>('');
  readonly seals = input<ICardSealPreview[]>([]);
  readonly filled = input<number>(0);

  readonly grid = computed(() => computeStampGrid(this.stampsRequired(), !!this.backText()));

  readonly rows = computed(() => {
    const g = this.grid();
    const total = g.rows.reduce((a, b) => a + b, 0);
    const seals = this.seals();
    const filledCount = this.filled();
    let index = 0;
    return g.rows.map(count => Array.from({ length: count }, () => {
      const i = index++;
      const isReward = i === total - 1;
      const filled = !isReward && i < filledCount;
      return {
        index: i,
        state: (isReward ? 'reward' : filled ? 'filled' : 'empty') as 'reward' | 'filled' | 'empty',
        seal: filled && seals.length ? seals[i % seals.length] : null,
      };
    }));
  });
}
